/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// src/components/Risorse/MezziAereiTab.tsx - AGGIORNATO con menu modale tipo
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { SelectModal } from '../common/SelectModal';
import { TipoMezzoAereo } from '../../types';

const POSIZIONI_OPERATIVE = [
  'In Avvicinamento',
  'Primo al Lancio',
  'Ha Lanciato',
  'In Rifornimento',
  'In Orbita',
  'Rientro Base',
  'May Day',
  'Annullato Lancio',
];

// TIPI MEZZI AEREI DETTAGLIATI
const TIPI_MEZZI_AEREI = [
  { value: 'canadair', label: 'Canadair CL-415/215', icon: '🛩️', category: 'ala_fissa', capacita: 6000 },
  { value: 'fireboss', label: 'Air Tractor Fireboss AT-802', icon: '✈️', category: 'ala_fissa', capacita: 3000 },
  { value: 's64', label: 'Sikorsky S-64 Skycrane', icon: '🚁', category: 'ala_rotante', capacita: 10000 },
  { value: 'kamov', label: 'Kamov Ka-32', icon: '🚁', category: 'ala_rotante', capacita: 4000 },
  { value: 'erickson', label: 'Erickson S-64E/F', icon: '🚁', category: 'ala_rotante', capacita: 9500 },
  { value: 'eli_medio', label: 'Elicottero Medio (AB-412, AW139)', icon: '🚁', category: 'ala_rotante', capacita: 1000 },
  { value: 'eli_leggero', label: 'Elicottero Leggero (AB-206, AS350)', icon: '🚁', category: 'ala_rotante', capacita: 600 },
  { value: 'altro', label: 'Altro (Specifica)', icon: '✈️', category: 'ala_fissa', capacita: 0 },
];

export const MezziAereiTab: React.FC = () => {
  const mezziAerei = useAppStore((state) => state.mezziAerei);
  const addMezzoAereo = useAppStore((state) => state.addMezzoAereo);
  const updateMezzoAereo = useAppStore((state) => state.updateMezzoAereo);
  const deleteMezzoAereo = useAppStore((state) => state.deleteMezzoAereo);
  
  const [formData, setFormData] = useState({
    sigla: '',
    tipo: 'ala_fissa' as TipoMezzoAereo,
    tipoDettaglio: '', // NUOVO: tipo specifico
    capacitaLitri: undefined as number | undefined,
    posizioneOperativa: '',
    note: '',
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showPosizioneModal, setShowPosizioneModal] = useState(false);
  const [showTipoModal, setShowTipoModal] = useState(false); // NUOVO

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let finalValue: any = value;
    if (type === 'number') {
      finalValue = value === '' ? undefined : parseInt(value);
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  // NUOVO: Gestione selezione tipo mezzo
  const handleTipoSelect = (tipoMezzo: typeof TIPI_MEZZI_AEREI[0]) => {
    setFormData({
      ...formData,
      tipoDettaglio: tipoMezzo.label,
      tipo: tipoMezzo.category as TipoMezzoAereo,
      capacitaLitri: tipoMezzo.capacita || formData.capacitaLitri
    });
    setShowTipoModal(false);
  };

  const handleSubmit = () => {
    if (!formData.sigla) {
      alert('⚠️ Sigla Mezzo è obbligatoria');
      return;
    }

    if (editingId) {
      updateMezzoAereo(editingId, formData as any);
      setEditingId(null);
    } else {
      addMezzoAereo(formData as any);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      sigla: '',
      tipo: 'ala_fissa',
      tipoDettaglio: '',
      capacitaLitri: undefined,
      posizioneOperativa: '',
      note: '',
    });
  };

  const handleEdit = (mezzo: any) => {
    setFormData({
      sigla: mezzo.sigla,
      tipo: mezzo.tipo,
      tipoDettaglio: mezzo.tipoDettaglio || '',
      capacitaLitri: mezzo.capacitaLitri,
      posizioneOperativa: mezzo.posizioneOperativa,
      note: mezzo.note || '',
    });
    setEditingId(mezzo.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    if (confirm('Sei sicuro di voler eliminare questo mezzo aereo?')) {
      deleteMezzoAereo(id);
    }
  };

  const alaFissa = mezziAerei.filter((m) => m.tipo === 'ala_fissa').length;
  const alaRotante = mezziAerei.filter((m) => m.tipo === 'ala_rotante').length;

  return (
    <div className="risorse-tab-content">
      <h3>🚁 Gestione Mezzi Aerei</h3>

      <div className="form-container">
        <h4>{editingId ? '✏️ Modifica Mezzo Aereo' : '➕ Nuovo Mezzo Aereo'}</h4>

        <div className="form-row">
          <div className="form-group">
            <label>Sigla Mezzo *</label>
            <input
              type="text"
              name="sigla"
              placeholder="es. DRAGO-101, CAN-23"
              value={formData.sigla}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label>Tipo Mezzo *</label>
            <input
              type="text"
              value={formData.tipoDettaglio || 'Seleziona tipo...'}
              placeholder="Seleziona tipo mezzo..."
              readOnly
              onClick={() => setShowTipoModal(true)}
              style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,107,53,0.3)', borderRadius: '8px', padding: '12px', color: '#fff' }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Capacità Estinguente (L)</label>
            <input
              type="number"
              name="capacitaLitri"
              min="0"
              placeholder="Auto-compilato dal tipo"
              value={formData.capacitaLitri || ''}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label>Posizione Operativa</label>
            <input
              type="text"
              value={formData.posizioneOperativa}
              placeholder="Seleziona posizione..."
              readOnly
              onClick={() => setShowPosizioneModal(true)}
              style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.1)', border: '2px solid rgba(255,107,53,0.3)', borderRadius: '8px', padding: '12px', color: '#fff' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Note</label>
          <textarea name="note" rows={2} value={formData.note} onChange={handleInputChange} />
        </div>

        <div className="form-actions">
          <button className="btn btn-success" onClick={handleSubmit}>
            {editingId ? '✏️ Salva Modifiche' : '💾 Salva Mezzo'}
          </button>
          {editingId && (
            <button className="btn btn-secondary" onClick={() => { resetForm(); setEditingId(null); }}>
              ✕ Annulla
            </button>
          )}
        </div>
      </div>

      <div className="lista-header">
        <h4>
          Lista Mezzi Aerei ({mezziAerei.length}) - Ala Fissa: {alaFissa} | Ala Rotante: {alaRotante}
        </h4>
      </div>

      <div className="risorse-list">
        {mezziAerei.length === 0 ? (
          <div className="empty-state">
            <p>🚁 Nessun mezzo aereo inserito</p>
          </div>
        ) : (
          (() => {
            // Ordinamento per priorità operativa
            const statusOrder: Record<string, number> = {
              'in_avvicinamento': 0,
              'may_day': 1,
              'primo_al_lancio': 2,
              'in_orbita': 3,
              'ha_lanciato': 4,
              'in_rifornimento': 5,
              'annullato_lancio': 6,
              'rientro_base': 7
            };

            const sortedMezzi = [...mezziAerei].sort((a, b) => {
              // Normalizziamo le posizioni operative convertendo in lowercase e sostituendo spazi con underscore
              const normalizeStatus = (status: string | undefined) => {
                if (!status) return '';
                return status.toLowerCase().replace(/\s+/g, '_');
              };
              
              const orderA = statusOrder[normalizeStatus(a.posizioneOperativa)] ?? 99;
              const orderB = statusOrder[normalizeStatus(b.posizioneOperativa)] ?? 99;
              return orderA - orderB;
            });

            return sortedMezzi.map((mezzo) => (
              <div key={mezzo.id} className="risorsa-card">
                <div className="card-header-risorsa">
                  <span className="icon">{mezzo.tipo === 'ala_fissa' ? '✈️' : '🚁'}</span>
                  <h4>
                    {mezzo.sigla} 
                    <span className={`badge status-${mezzo.posizioneOperativa?.toLowerCase().replace(/\s/g, '_')}`}>{mezzo.posizioneOperativa}</span>
                  </h4>
                </div>
                <div className="card-body-risorsa">
                  <p><strong>Tipo:</strong> {(mezzo as any).tipoDettaglio || (mezzo.tipo === 'ala_fissa' ? 'Canadair' : 'Elicottero')}</p>
                  {mezzo.capacitaLitri && <p><strong>Capacità:</strong> {mezzo.capacitaLitri}L</p>}
                  {mezzo.note && <p><strong>Note:</strong> {mezzo.note}</p>}
                </div>
                <div className="card-actions">
                  <button className="btn-edit" onClick={() => handleEdit(mezzo)}>✏️ Modifica</button>
                  <button className="btn-delete" onClick={() => handleDelete(mezzo.id)}>🗑️ Elimina</button>
                </div>
              </div>
            ));
          })()
        )}
      </div>

      {/* MODALE POSIZIONE OPERATIVA */}
      <SelectModal
        isOpen={showPosizioneModal}
        onClose={() => setShowPosizioneModal(false)}
        title="Seleziona Posizione Operativa"
        options={POSIZIONI_OPERATIVE}
        selectedValue={formData.posizioneOperativa}
        onSelect={(value) => setFormData({ ...formData, posizioneOperativa: value })}
      />

      {/* MODALE TIPO MEZZO - NUOVO */}
      {showTipoModal && (
        <div className="modal-overlay" onClick={() => setShowTipoModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✈️ SELEZIONA TIPO MEZZO AEREO</h3>
              <button className="btn-close" onClick={() => setShowTipoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',gap:'15px'}}>
                {TIPI_MEZZI_AEREI.map(tipo => (
                  <button
                    key={tipo.value}
                    onClick={() => handleTipoSelect(tipo)}
                    style={{
                      padding:'20px',
                      background: formData.tipoDettaglio === tipo.label ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.05)',
                      border: `3px solid ${formData.tipoDettaglio === tipo.label ? '#2ecc71' : 'rgba(255,107,53,0.3)'}`,
                      borderRadius:'12px',
                      color:'#fff',
                      textAlign:'left',
                      cursor:'pointer',
                      transition:'all 0.3s',
                      display:'flex',
                      flexDirection:'column',
                      gap:'10px'
                    }}
                  >
                    <div style={{fontSize:'32px'}}>{tipo.icon}</div>
                    <div style={{fontSize:'16px',fontWeight:'700'}}>{tipo.label}</div>
                    <div style={{fontSize:'13px',color:'#aaa'}}>
                      {tipo.category === 'ala_fissa' ? '✈️ Ala Fissa' : '🚁 Ala Rotante'}
                    </div>
                    {tipo.capacita > 0 && (
                      <div style={{fontSize:'14px',color:'#3498db',fontWeight:'600'}}>
                        💧 Capacità: {tipo.capacita.toLocaleString()}L
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
